import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthLayout, Button } from '../components';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../services/api';
import { authenticatedHomePath } from '../utils/onboarding';
import { isRequestTimeout } from '../utils/apiError';
import type { InvitationDetails } from '../types';
import { sameDisplayName } from '../utils/displayName';
import { invitationErrorKey } from '../utils/invitationLink';
import { InvitationCompanyFacts } from '../components/OpenInvitations';

const acceptedInvitationPath = (invitation: InvitationDetails) =>
  invitation.type === 'customer' ? '/my-companies' : '/customers';

export function InvitationDecision() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, logout, refreshUser } = useAuth();
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const invitationEmail = invitation?.contactEmail || invitation?.customerEmail || '';
  const invitationPersona = !invitation || invitation.type === 'customer' ? 'person' : invitation.type;
  const invitationAccepted = invitation?.status === 'accepted';
  const emailMismatch = Boolean(
    user?.email &&
      invitationEmail &&
      user.email.trim().toLowerCase() !== invitationEmail.trim().toLowerCase()
  );
  // A client invitation is for a Beställare account only (ClickUp 869fmx898).
  const personaMismatch = Boolean(
    invitation?.type === 'customer' && user?.personaType && user.personaType !== 'person'
  );
  const wrongAccount = emailMismatch || personaMismatch;
  const companyDetails = invitation?.type === 'customer'
    ? {
        name: invitation.company?.name || invitation.companyName || '',
        contact: invitation.inviterName?.trim() && !sameDisplayName(invitation.inviterName, invitation.company?.name || invitation.companyName)
          ? invitation.inviterName
          : '',
        city: invitation.company?.city || '',
        organizationNumber: invitation.company?.organizationNumber || ''
      }
    : null;

  const emailMismatchMessage = t(
    'invitationDecision.emailMismatchDescription',
    'Inbjudan gäller {{invitedEmail}}, men du är inloggad som {{signedInEmail}}. Byt konto för att fortsätta.',
    {
      invitedEmail: invitationEmail,
      signedInEmail: user?.email || ''
    }
  );

  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    authApi
      .getInvitation(id)
      .then((data) => {
        if (data.status === 'accepted') {
          setInvitation(data);
          const recipientEmail = data.contactEmail || data.customerEmail || '';
          if (
            user?.email &&
            recipientEmail &&
            user.email.trim().toLowerCase() === recipientEmail.trim().toLowerCase()
          ) {
            navigate(acceptedInvitationPath(data), { replace: true });
          }
          return;
        }
        if (data.linkState && data.linkState !== 'active') {
          setError(t(invitationErrorKey(data.linkState)));
          return;
        }
        setInvitation(data);
      })
      .catch(() => setError(t('auth.register.invitationInvalid')))
      .finally(() => setIsLoading(false));
  }, [id, navigate, t, user?.email]);

  const accept = async () => {
    if (!id || wrongAccount) return;
    setIsSubmitting(true);
    setError('');
    try {
      const result = await authApi.acceptExistingInvitation(id);
      const refreshedUser = await refreshUser();
      navigate(
        result.requiresOnboarding
          ? '/onboarding'
          : authenticatedHomePath(refreshedUser ?? user)
      );
    } catch (err: any) {
      const apiError = err.response?.data?.error;
      setError(
        isRequestTimeout(err)
          ? t('common.saveTimedOut')
          : apiError?.code === 'INVITATION_EMAIL_MISMATCH'
          ? emailMismatchMessage
          : apiError?.code === 'INVITATION_PERSONA_MISMATCH'
          ? t('invitationDecision.personaMismatchDescription')
          : apiError?.code?.startsWith('INVITATION_')
          ? t(
              invitationErrorKey(
                apiError.code
                  .replace('INVITATION_', '')
                  .toLowerCase() as InvitationDetails['linkState']
              )
            )
          : apiError?.message || t('common.error')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const decline = async () => {
    if (!id || wrongAccount) return;
    setIsSubmitting(true);
    setError('');
    try {
      await authApi.declineExistingInvitation(id);
      navigate(authenticatedHomePath(user));
    } catch (err: any) {
      const apiError = err.response?.data?.error;
      setError(isRequestTimeout(err) ? t('common.saveTimedOut') : apiError?.message || t('common.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const switchAccount = () => {
    if (!id) return;
    logout();
    navigate(
      invitationAccepted
        ? `/login?invitation=${encodeURIComponent(id)}`
        : `/register?persona=${invitationPersona}&invitation=${encodeURIComponent(id)}`,
      { replace: true }
    );
  };

  const title =
    invitation?.type === 'company'
      ? invitation.companyName
      : invitation?.companyName || invitation?.inviterName;

  return (
    <AuthLayout backgroundImage="/assets/images/auth/auth-cover-register-bg.svg">
      <div className="wd-50 mb-5">
        <img src="/assets/images/logo-abbr.png" alt="ETAPPIO" className="img-fluid" />
      </div>
      <h2 className="fs-20 fw-bolder mb-4">
        {invitationAccepted
          ? t(
              'invitationDecision.acceptedTitle',
              'Inbjudan har redan accepterats'
            )
          : t('invitationDecision.title', 'Acceptera inbjudan?')}
      </h2>
      <p className="fs-12 fw-medium text-muted">
        {invitationAccepted
          ? t(
              'invitationDecision.acceptedDescription',
              'Logga in med kontot som accepterade inbjudan för att se kopplingen i Etappio.'
            )
          : t(
              'invitationDecision.description',
              'Välj om du vill koppla ditt befintliga konto till inbjudan.'
            )}
      </p>

      {error && (
        <div className="alert alert-danger mt-3" role="alert">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="spinner-border text-primary mt-4" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      ) : (
        <div className="w-100 mt-4">
          {invitation && (
            <div className="alert alert-info">
              {invitation.type === 'company'
                ? t(
                    'invitationDecision.companyInvitation',
                    'Du har blivit inbjuden av {{inviter}} att koppla företaget {{company}}.',
                    {
                      inviter: invitation.inviterName || invitation.customerName || '',
                      company: title || ''
                    }
                  )
                : invitation.inviterName?.trim() &&
                    !sameDisplayName(invitation.inviterName, invitation.companyName)
                  ? t(
                      'invitationDecision.customerInvitationFromPerson',
                      'Du har blivit inbjuden av {{inviter}} från {{company}} att koppla ditt konto.',
                      {
                        inviter: invitation.inviterName,
                        company: invitation.companyName || ''
                      }
                    )
                  : t(
                      'invitationDecision.customerInvitation',
                      'Du har blivit inbjuden att koppla ditt konto till {{company}}.',
                      { company: title || '' }
                    )}
            </div>
          )}
          {companyDetails && !invitationAccepted && (
            <div className="invitation-company card mb-3" data-testid="invitation-company">
              <div className="card-body">
                <p className="invitation-company-kicker mb-1">{t('invitationDecision.company.kicker')}</p>
                <p className="fw-bold mb-2">{companyDetails.name}</p>
                <InvitationCompanyFacts
                  contact={companyDetails.contact}
                  city={companyDetails.city}
                  organizationNumber={companyDetails.organizationNumber}
                />
                <p className="small text-muted mb-0" data-testid="invitation-company-explanation">
                  {t('invitationDecision.company.explanation', { company: companyDetails.name })}
                </p>
              </div>
            </div>
          )}
          {invitation && personaMismatch && !emailMismatch && (
            <div className="alert alert-warning" role="alert" data-testid="invitation-persona-mismatch">
              <div className="fw-bold mb-1">{t('invitationDecision.personaMismatchTitle')}</div>
              <div>{t('invitationDecision.personaMismatchDescription')}</div>
            </div>
          )}
          {invitation && emailMismatch && (
            <div className="alert alert-warning" role="alert">
              <div className="fw-bold mb-1">
                {t(
                  'invitationDecision.emailMismatchTitle',
                  'Du är inloggad med fel konto'
                )}
              </div>
              <div>{emailMismatchMessage}</div>
            </div>
          )}
          {invitation && !invitationAccepted && (
            <div className="d-grid gap-2">
              {personaMismatch && !emailMismatch ? (
                <Button type="button" size="lg" fullWidth onClick={() => navigate(authenticatedHomePath(user))}>
                  {t('invitationDecision.backHome')}
                </Button>
              ) : wrongAccount ? (
                <Button type="button" size="lg" fullWidth onClick={switchAccount}>
                  {t(
                    'invitationDecision.continueWithInvitation',
                    'Fortsätt med inbjudan'
                  )}
                </Button>
              ) : (
                <>
                  <Button type="button" size="lg" fullWidth isLoading={isSubmitting} onClick={accept}>
                    {t('invitationDecision.accept', 'Acceptera')}
                  </Button>
                  <Button type="button" variant="secondary" size="lg" fullWidth onClick={decline}>
                    {t('invitationDecision.decline', 'Avböj')}
                  </Button>
                </>
              )}
            </div>
          )}
          {invitationAccepted && emailMismatch && (
            <div className="d-grid gap-2">
              <Button type="button" size="lg" fullWidth onClick={switchAccount}>
                {t(
                  'invitationDecision.signInWithInvitedAccount',
                  'Logga in med rätt konto'
                )}
              </Button>
            </div>
          )}
        </div>
      )}
    </AuthLayout>
  );
}
